import { Component } from '@angular/core';
import { OnInit, Input } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { AlertsService } from '../../services/alerts.service';
import { UserService } from '../../services/user.service';
import { ProductService } from '../../services/product.service';
import { TagsService } from '../../services/tags.service';
import { SearchService } from '../../search/services/search.service';
import { JsonPipe } from '@angular/common';

// For typeahead functionality
import { Observable, OperatorFunction, from } from 'rxjs';
import { debounceTime, distinctUntilChanged, switchMap } from 'rxjs/operators';

/**
 * Component for managing topical tags for a product.
 */
@Component({
	selector: 'tags',
	templateUrl: './tags.component.html',
	styleUrl: './tags.component.css'
})
export class TagsComponent implements OnInit {
	@Input() product: any;

	LOCALIZATION: any;
	userPermissions: { [key: string]: any } = {};

	isready: boolean = false;
	loading: boolean = false;

	/*  Example tag = {name: tagvalue, field: fieldname} */
	tags: any[] = [];

	relation = { name: 'topic_tag', label: 'Tag', tagField: 'topic_tag', value: '', show: false };

	tagSearch = {
		relation: {} as any,
		related: [] as any[]
	};

	constructor(
		private userService: UserService,
		private tagsService: TagsService,
		private alertsService: AlertsService,
		private searchService: SearchService,
		private productService: ProductService,
		private ngbmodalService: NgbActiveModal
	) { }

	ngOnInit(): void {
		this.LOCALIZATION = this.userService.LOCALIZATION;
		this.userPermissions = this.userService.getUserPermissionsMap();

		this.initFields();
		this.loadTags();
	}

	private initFields(): void {
		let found = this.userService.user.properties.awfieldObjectList.find((field: any) => {
			return this.relation.name === field.name;
		});
		if (found && found.isAvailable) {
			this.relation.label = found.displayName;
			this.relation.show = true;
			this.isready = true;
		}
	}

	private loadTags(): void {
		let tagfields = [];
		if (this.relation.show) {
			tagfields.push(this.relation.tagField);
		}
		if (tagfields.length == 0) {
			return;
		}

		this.tagsService.getTags(this.product.id, tagfields).then(
			(response: any) => {
				console.log(response);

				// normalize the results
				for (let j = 0; j < response.length; j++) {
					if (response[j].field === this.relation.tagField) {
						response[j].field = this.relation.name;
					}
				}
				this.tags = response;
			},
			(err: any) => {
				console.error("Failed to load product topic tags", err);
				this.alertsService.error({
					message: "Failed to retrieve tags for this document."
				});
			});
	}

	save(): void {
		var payload = {
			replaceAll: false,
			replaceFieldValues: true,
			metadata: {} as { [key: string]: any },
			id: this.product.id
		};

		if (this.relation.show) {
			// Only these values were potentially changed
			var collect = this.tags.filter((t: any) => {
				return t.field === this.relation.name;
			});
			let tagnames = collect.map((c: any) => {
				return c.name;
			});
			payload.metadata[this.relation.tagField] = tagnames;
		}

		console.log(payload);

		this.productService.updateMetadata(payload).then(
			(response: any) => {
				console.log("Updated metadata for document, id=%s", payload.id);
				this.alertsService.success({
					message: this.LOCALIZATION.searchUpdatesConfirmationMessage,
					delay: 5000
				});

			},
			(err: any) => {
				console.error("Failed to save tags for document", err);
				this.alertsService.error({
					message: "Failed to update tags."
				});
			}
		);
	}

	// Converts the service Promise to an Observable emitting a string array
	private getSuggestions(fieldid: string, val: string): Observable<string[]> {
		// searchService returns a Promise
		return from(this.searchService.getTermSuggestions(fieldid, val)) as Observable<string[]>;
	}

	/**
	 * Typeahead search handler
	 */
	search = (text$: Observable<string>) =>
		text$.pipe(
			debounceTime(200),
			distinctUntilChanged(),
			switchMap((term) => {
				console.log('Received term:', term);
				return from(this.getSuggestions(this.relation.tagField, term))
			})
		)

	searchRelated(relation: any): void {
		let payload = {
			field: relation.name,
			value: relation.value
		};
		this.searchService.broadcastSearchResultFieldEvent(payload);

		this.close();
	}

	/**
	 * Searches for documents having the given tag.
	 */
	searchTag(tag: any): void {
		this.loading = true;

		this.relation.value = tag.name;
		this.tagsService.searchTags(this.relation, this.product.id).then(
			(response: any) => {
				this.tagSearch = {
					relation: this.relation,
					related: response
				}
				this.loading = false;
			},
			(err: any) => {
				console.error("Tag search failed", err);
				this.alertsService.error({
					message: "Failed to retrieve tags for this document."
				});
				this.loading = false;
			}
		);
	}

	removeTag(tag: any): void {
		this.tags = this.tags.filter(function(t) {
			return !(t.name === tag.name && t.field === tag.field);
		});
	}

	addTag(): void {
		if (this.relation.value != null && this.relation.value.trim() != "") {
			this.relation.value = this.relation.value.trim();
			var dupecheck = this.tags.filter((t: any) => {
				return t.name === this.relation.value && t.field === this.relation.name;
			});
			if (dupecheck.length == 0) {
				this.tags.push({ name: this.relation.value, field: this.relation.name });
			}
		}
	}

	/**
	 * Closes the modal.
	 */
	close(): void {
		this.ngbmodalService.dismiss();
	}
}
